import Button, { ButtonTypes } from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent } from "molecules/modals/modal";
import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Heading from "atoms/typography/heading";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import { t } from "utilities/localization-utils";
import useChecklistItemNote from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item-note";
import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";
import { ChecklistItemNoteSectionRecord } from "models/view-models/checklist-item-notes/checklist-item-note-section-record";
import useModalActions from "utilities/hooks/use-modal-actions";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import DeprecatedHide from "atoms/wrappers/hide";
import { ToastManager } from "utilities/toast/toast-manager";
import { atom, useAtom } from "jotai";
import StringUtils from "utilities/string-utils";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import ChecklistItemNoteSectionsSelect from "organisms/checklist-item-note-sections/checklist-item-note-sections-select";
import SectionRecord from "models/view-models/section-record";
import useChecklistItemNoteSection from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item-note-section";
import ChecklistItemNoteRichTextEditor from "organisms/checklist-item-notes/checklist-item-note-rich-text-editor";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import useQuillImageTagSanitizer from "utilities/hooks/quill/use-quill-image-tag-sanitizer";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import useMostRecentlyEditedOn from "utilities/hooks/domain/notebooks/pages/use-most-recently-edited-on";
import usePageLimits from "pages/notebooks/use-page-limits";

interface ChecklistItemNoteModalProps {
    checklistItemId: number;
    closeDialog: (event?: CloseDialogEvent) => void;
    existingChecklistItemNote?: ChecklistItemNoteRecord;
    isVisible: boolean;
    onDelete: () => void;
    onSave: (updatedNote: ChecklistItemNoteRecord) => void;
}

const BASE_CLASS_NAME = "c-checklist-item-note-modal";

export const ChecklistItemNoteModalRefAtom = atom<RefObject<HTMLElement>>();

const ChecklistItemNoteModal: React.FC<ChecklistItemNoteModalProps> = ({
    checklistItemId,
    closeDialog,
    existingChecklistItemNote,
    isVisible,
    onDelete,
    onSave,
}) => {
    const {
        createChecklistItemNote,
        deleteChecklistItemNote,
        updateChecklistItemNote,
    } = useChecklistItemNote();
    const { createChecklistItemNoteSection, deleteChecklistItemNoteSection } =
        useChecklistItemNoteSection();
    const { isLaptop } = useNotebookBreakpoints();
    const { currentPage } = useCurrentPage();
    const { refreshMostRecentlyEditedOn } = useMostRecentlyEditedOn(
        currentPage?.id
    );
    const { restoreImageRichTextContent } = useQuillImageTagSanitizer();
    const {
        isOpen: isDeleteConfirmationModalOpen,
        handleClose: handleDeleteConfirmationModalClose,
        handleOpen: handleDeleteConfirmationModalOpen,
    } = useModalActions();
    const { pageLimits } = usePageLimits();

    const [contentRestored, setContentRestored] = useState(false);
    const [noteText, setNoteText] = useState("");
    const modalRef = useRef<HTMLDivElement>(null);
    const [selectedSections, setSelectedSections] = useState<
        Array<SectionRecord>
    >([]);
    const [initialSections, setInitialSections] = useState<
        Array<SectionRecord>
    >([]);
    const [sectionSelectActive, setSectionSelectActive] = useState(false);

    const isNewNote =
        existingChecklistItemNote?.id == null ||
        existingChecklistItemNote.id === 0;
    const title = isNewNote ? t("addNote") : t("editNote");

    useEffect(() => {
        if (existingChecklistItemNote?.checklistItemNoteSections) {
            const sections = existingChecklistItemNote.checklistItemNoteSections
                .map((noteSection) => noteSection.section)
                .filter((section): section is SectionRecord => section != null);

            setSelectedSections(sections);
            setInitialSections(sections);
        }
    }, [existingChecklistItemNote]);

    useAtom(
        useMemo(() => {
            const a = atom(
                (get) => get(ChecklistItemNoteModalRefAtom),
                (_, set, arg: RefObject<HTMLElement>) =>
                    set(ChecklistItemNoteModalRefAtom, arg)
            );
            a.onMount = (set) => {
                set(modalRef);
            };
            return a;
        }, [])
    );

    useEffect(() => {
        if (contentRestored) return;
        if (!noteText) return;

        setContentRestored(true);
        (async () => {
            const restored = await restoreImageRichTextContent(noteText);
            setNoteText(restored);
        })();
    }, [contentRestored, noteText, restoreImageRichTextContent]);

    const createNote = async (): Promise<void> => {
        let createdNote = await createChecklistItemNote(
            checklistItemId,
            noteText
        );

        if (createdNote == null) {
            ToastManager.error(t("errorCreatingChecklistItemNote"));

            return;
        }

        const createdSections = await createNoteSection(
            createdNote.id!,
            selectedSections
        );

        createdNote = createdNote.with({
            checklistItemNoteSections: createdSections,
        });
        await refreshMostRecentlyEditedOn();

        ToastManager.success(t("noteCreatedSuccessfully"));

        onSave(createdNote);
    };
    const createNoteSection = async (
        checklistItemNoteId: number,
        sections: Array<SectionRecord>
    ): Promise<ChecklistItemNoteSectionRecord[]> => {
        if (sections.length === 0) {
            return [];
        }
        const createdRecords: ChecklistItemNoteSectionRecord[] = [];
        for (const section of sections) {
            const created = await createChecklistItemNoteSection(
                checklistItemNoteId,
                section.id!
            );
            if (created) {
                const createdWithSection = created.with({ section: section });
                createdRecords.push(createdWithSection);
                await refreshMostRecentlyEditedOn();
            }
        }
        return createdRecords;
    };

    const deleteNoteSections = async (
        sections: Array<SectionRecord>
    ): Promise<ChecklistItemNoteSectionRecord[]> => {
        if (sections.length === 0 || !existingChecklistItemNote) {
            return [];
        }
        const deletedRecords: ChecklistItemNoteSectionRecord[] = [];
        for (const section of sections) {
            const noteSectionRecord =
                existingChecklistItemNote.checklistItemNoteSections?.find(
                    (ns) => ns.sectionId === section.id
                );
            if (noteSectionRecord?.id) {
                await deleteChecklistItemNoteSection(noteSectionRecord.id);
                deletedRecords.push(noteSectionRecord);
                await refreshMostRecentlyEditedOn();
            }
        }
        return deletedRecords;
    };

    const handleSectionSelected = (section: SectionRecord): void => {
        setSelectedSections([...selectedSections, section]);
    };

    const handleSectionRemoved = (section: SectionRecord): void => {
        const updatedSections = selectedSections.filter(
            (s) => s.id !== section.id
        );
        setSelectedSections(updatedSections);
    };

    const updateNoteSections = async (
        noteId: number
    ): Promise<ChecklistItemNoteSectionRecord[]> => {
        if (existingChecklistItemNote == null) {
            return [];
        }

        const sectionsToAdd = selectedSections.filter(
            (section) =>
                !initialSections.some((initial) => initial.id === section.id)
        );

        const sectionsToDelete = initialSections.filter(
            (initial) =>
                !selectedSections.some((section) => section.id === initial.id)
        );

        const deletedRecords = await deleteNoteSections(sectionsToDelete);

        const createdRecords = await createNoteSection(noteId, sectionsToAdd);

        const remainingNoteSections =
            existingChecklistItemNote.checklistItemNoteSections?.filter(
                (noteSection) =>
                    !deletedRecords.some(
                        (deleted) => deleted.id === noteSection.id
                    )
            ) ?? [];

        const updatedNoteSections = [
            ...remainingNoteSections,
            ...createdRecords,
        ];

        const remainingSections = initialSections.filter(
            (initial) =>
                !sectionsToDelete.some((deleted) => deleted.id === initial.id)
        );
        const newSectionsList = [...remainingSections, ...sectionsToAdd];

        setInitialSections(newSectionsList);
        setSelectedSections(newSectionsList);

        return updatedNoteSections;
    };

    const updateNote = async (): Promise<void> => {
        if (existingChecklistItemNote == null) {
            return;
        }

        const noteToUpdate = existingChecklistItemNote.with({ text: noteText });
        let updatedNote = await updateChecklistItemNote(noteToUpdate);

        if (updatedNote == null) {
            ToastManager.error(t("errorUpdatingChecklistItemNote"));
            return;
        }
        const newCodeReferences = await updateNoteSections(updatedNote.id!);

        updatedNote = updatedNote.with({
            checklistItemNoteSections: newCodeReferences,
        });

        ToastManager.success(t("noteUpdatedSuccessfully"));
        await refreshMostRecentlyEditedOn();
        setContentRestored(false);
        onSave(updatedNote);
    };

    const resetTextArea = () => {
        setNoteText("");
    };

    const resetForm = () => {
        resetTextArea();
        setSelectedSections(initialSections);
    };

    const closeModal = () => {
        setContentRestored(false);
        resetForm();
        closeDialog();
    };

    const handleCancelClick = () => {
        closeModal();
    };

    const handleChange = (newValue: string): void => {
        setNoteText(newValue);
    };

    const handleDeleteClick = async () => {
        try {
            await deleteChecklistItemNote(existingChecklistItemNote);
            await refreshMostRecentlyEditedOn();

            ToastManager.success(t("noteDeletedSuccessfully"));

            handleDeleteConfirmationModalClose();
            closeModal();

            onDelete();
        } catch (error) {
            console.error("Error deleting checklist item note:", error);

            ToastManager.error(t("errorDeletingChecklistItemNote"));
        }
    };

    const handleSaveClick = async (): Promise<void> => {
        const apiCall = isNewNote ? createNote : updateNote;

        await apiCall();

        closeModal();
    };

    const handleSectionSelectActive = (isActive: boolean) => {
        setSectionSelectActive(isActive);
    };

    useEffect(() => {
        if (
            isVisible &&
            StringUtils.hasValue(existingChecklistItemNote?.text)
        ) {
            setNoteText(existingChecklistItemNote.text);
        }
    }, [existingChecklistItemNote?.text, isVisible]);

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeModal}
            cssClassName={BASE_CLASS_NAME}
            isVisible={isVisible}
            label={t("checklist-item-note")}
            ref={modalRef}
            stopEventPropagation={true}
            type={isLaptop ? ModalTypes.Bottom : ModalTypes.Base}>
            <Heading cssClassName={`${BASE_CLASS_NAME}__heading`}>
                <div className={`${BASE_CLASS_NAME}__heading__title`}>
                    <Icon
                        type={Icons.Message}
                        size={IconSizes.Base}
                        cssClassName={`${BASE_CLASS_NAME}__heading__icon`}
                    />
                    <div>{title}</div>
                </div>
                <Button
                    accessibleText={t("close")}
                    cssClassName="-modal-close -icon"
                    onClick={closeModal}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.TertiaryAlt}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <div className={`${BASE_CLASS_NAME}__content`}>
                <div className={`${BASE_CLASS_NAME}__label`}>{t("note")}</div>
                <ChecklistItemNoteRichTextEditor
                    fileUploadDestination={FileUploadDestination.NotebookImage}
                    onChange={handleChange}
                    preserveWhitespace={true}
                    value={noteText}
                    maxLength={pageLimits?.checklistItemNoteCharacterLimit}
                />
                <ChecklistItemNoteSectionsSelect
                    onSelected={handleSectionSelected}
                    sections={selectedSections}
                    onRemoved={handleSectionRemoved}
                    onSelectIsActive={handleSectionSelectActive}
                />
            </div>
            <div className={`${BASE_CLASS_NAME}__buttons`}>
                <div className={`${BASE_CLASS_NAME}__buttons__left-side`}>
                    <DeprecatedHide when={isNewNote}>
                        <Button
                            accessibleText={t("delete")}
                            cssClassName={`${BASE_CLASS_NAME}__delete-button`}
                            onClick={handleDeleteConfirmationModalOpen}
                            style={ButtonStyles.Destructive}>
                            {t("delete")}
                        </Button>
                    </DeprecatedHide>
                </div>
                <div className={`${BASE_CLASS_NAME}__buttons__right-side`}>
                    <Button
                        accessibleText={t("cancel")}
                        onClick={handleCancelClick}
                        style={ButtonStyles.Secondary}>
                        {t("cancel")}
                    </Button>
                    <Button
                        accessibleText={t("create")}
                        disabled={sectionSelectActive}
                        style={ButtonStyles.Primary}
                        cssClassName={`${BASE_CLASS_NAME}__create-button`}
                        type={ButtonTypes.Submit}
                        onClick={handleSaveClick}>
                        {t("save")}
                    </Button>
                </div>
            </div>
            <ConfirmationModal
                isVisible={isDeleteConfirmationModalOpen}
                message={t("confirmationMessage", {
                    action: t("delete").toLowerCase(),
                    item: t("note"),
                })}
                onCancel={handleDeleteConfirmationModalClose}
                onConfirm={handleDeleteClick}
            />
        </Modal>
    );
};

export default ChecklistItemNoteModal;
